import React, { useState, useEffect } from 'react';
import { useBalance } from '../context/BalanceContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import depositeImage from '../assest/images/deposite-img.jpg';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, CheckCircle, Clock, XCircle, ChevronDown, Copy, UploadCloud, ArrowRight, Loader } from 'lucide-react';

const DepositPage = () => {
  const { totalBalance, fetchBalance } = useBalance();
  const [amount, setAmount] = useState('');
  const [showDepositSection, setShowDepositSection] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [copyTooltip, setCopyTooltip] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [depositAddresses, setDepositAddresses] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('deposit');
  const [error, setError] = useState(null);
  const [depositHistory, setDepositHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupImageUrl, setPopupImageUrl] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [networkSelectOpen, setNetworkSelectOpen] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [submitAnimation, setSubmitAnimation] = useState(false);

  useEffect(() => {
    const fetchDepositAddresses = async () => {
      try {
        const response = await axios.get('/api/deposit-addresses');
        setDepositAddresses(response.data);
        if (response.data.length > 0) {
          setSelectedNetwork(response.data[0].network);
        }
      } catch (error) {
        console.error('Failed to fetch deposit addresses:', error);
      }
    };
    fetchDepositAddresses();
  }, []);

  const negativeBalance = totalBalance < 0 ? Math.abs(totalBalance) : 0;
  const currentAddress = depositAddresses.find(a => a.network === selectedNetwork)?.address;

  const openImagePopup = (imageUrl) => {
    setPopupImageUrl(imageUrl);
    setIsPopupOpen(true);
  };

  const closeImagePopup = () => {
    setIsPopupOpen(false);
    setPopupImageUrl('');
  };

  useEffect(() => {
    if (activeTab === 'history' && user) {
      const fetchDepositHistory = async () => {
        setHistoryLoading(true);
        setHistoryError(null);
        try {
          const token = localStorage.getItem('token');
          const config = {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
          const res = await axios.get('/api/deposits/my-history', config);
          setDepositHistory(res.data);
          setHistoryLoading(false);
        } catch (err) {
          console.error('Error fetching deposit history:', err);
          setHistoryError('Failed to load deposit history.');
          setHistoryLoading(false);
        }
      };

      fetchDepositHistory();
    }
  }, [activeTab, user]);

  useEffect(() => {
    // Fetch balance when the component mounts
    fetchBalance();
  }, [fetchBalance]);

  const handleButtonClick = (val) => {
    setAmount(val.toFixed(2));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (amount && parseFloat(amount) > 0) {
      setShowDepositSection(true);
      setError(null);
    } else {
      setError('Please enter a valid deposit amount.');
      setShowDepositSection(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setFileError('');
    setPreviewLoaded(false);
  };

  const handleImageLoad = () => {
    setPreviewLoaded(true);
  };

  const handleCompletePayment = async (e) => {
    e.preventDefault();
    if (!file) {
      setFileError('Attachment is required.');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append('amount', amount);
    formData.append('network', selectedNetwork);
    formData.append('screenshot', file);

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      };
      const res = await axios.post('/api/deposits/request', formData, config);

      console.log('Deposit request submitted:', res.data);

      setAmount('');
      setFile(null);
      setShowDepositSection(false);
      setIsSubmitting(false);
      navigate('/transactions');
    } catch (err) {
      console.error('Error submitting deposit request:', err);
      setError('Failed to submit deposit request.');
      setIsSubmitting(false);
    }
  };

  const handleCopyAddress = () => {
    if (!currentAddress) return;
    navigator.clipboard.writeText(currentAddress);
    setCopyTooltip(true);
    setTimeout(() => {
      setCopyTooltip(false);
    }, 1200);
  };

  const formatDateTime = (dateString) => {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (status) => {
    switch (status ? status.toLowerCase() : '') {
      case 'paid':
      case 'completed':
        return { text: 'Completed', color: 'text-green-400', bgColor: 'bg-green-500/10', icon: <CheckCircle size={14} /> };
      case 'rejected':
        return { text: 'Rejected', color: 'text-red-400', bgColor: 'bg-red-500/10', icon: <XCircle size={14} /> };
      case 'to be reviewed':
      case 'pending':
      default:
        return { text: 'Pending', color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', icon: <Clock size={14} /> };
    }
  };

  return (
    <>
    <Helmet>
      <title>Deposit USDT | RatingApp</title>
      <meta name="description" content="Fund your RatingApp account with USDT to unlock app rating tasks and start earning daily profits." />
    </Helmet>
    <div className="w-full pb-24 text-white font-sans">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.h1 
          className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 text-center mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Deposit USDT
        </motion.h1>
        <p className="text-slate-400 text-center mb-8">Add funds to your account to continue rating.</p>

        {/* Tabs */}
        <motion.div
          className="text-white flex justify-center gap-2 md:gap-4 mb-8 bg-gray-800/20 backdrop-blur-xl border border-white/10 p-2 rounded-2xl"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <motion.button
            className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-colors duration-300 relative ${
              activeTab === 'deposit' ? 'text-white' : 'text-slate-400 hover:bg-white/5'
            }`}
            onClick={() => setActiveTab('deposit')}
            whileTap={{ scale: 0.95 }}
          >
            Deposit Now
            {activeTab === 'deposit' && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg -z-10"
                layoutId="activeDepositTab"
              />
            )}
          </motion.button>
          <motion.button
            className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-colors duration-300 relative ${
              activeTab === 'history' ? 'text-white' : 'text-slate-400 hover:bg-white/5'
            }`}
            onClick={() => setActiveTab('history')}
            whileTap={{ scale: 0.95 }}
          >
            Deposit History
            {activeTab === 'history' && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg -z-10"
                layoutId="activeDepositTab"
              />
            )}
          </motion.button>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === 'deposit' ? (
            <motion.div
              key="deposit-form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Balance Display */}
              <motion.div className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8 text-center">
                <p className="text-slate-400 text-sm mb-1">Total Balance</p>
                <p className="text-4xl font-bold tracking-tight text-white flex items-center justify-center gap-2">
                  <Wallet size={32} className="text-cyan-400" />
                  {totalBalance.toFixed(2)}
                  <span className="text-2xl text-slate-400">USDT</span>
                </p>
              </motion.div>

              {!showDepositSection ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-3">Select Deposit Amount</h2>
                    <div className="grid grid-cols-3 gap-3">
                      {[100, 300].map(val => (
                        <motion.button type="button" key={val} onClick={() => handleButtonClick(val)}
                          className={`p-4 rounded-xl border transition-all duration-200 ${amount === val.toFixed(2) ? 'bg-cyan-500/20 border-cyan-400' : 'bg-gray-800/20 border-white/10 hover:border-white/20'}`}
                          whileTap={{ scale: 0.95 }}
                        >
                          <p className="text-xl font-bold text-white">{val.toFixed(2)}</p>
                          <p className="text-xs text-slate-400">Receive {val}</p>
                        </motion.button>
                      ))}
                      <motion.button type="button" onClick={() => handleButtonClick(negativeBalance)}
                        className={`p-4 rounded-xl border transition-all duration-200 ${amount === negativeBalance.toFixed(2) ? 'bg-cyan-500/20 border-cyan-400' : 'bg-gray-800/20 border-white/10 hover:border-white/20'}`}
                        whileTap={{ scale: 0.95 }}
                        disabled={negativeBalance <= 0}
                      >
                        <p className="text-xl font-bold text-white">{negativeBalance.toFixed(2)}</p>
                        <p className="text-xs text-slate-400">Top up Negative</p>
                      </motion.button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="depositAmount" className="block text-lg font-semibold text-white mb-3">Or Enter Amount</label>
                    <div className="relative">
                      <input
                        id="depositAmount"
                        type="number"
                        step="0.01"
                        placeholder="Enter amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-xl py-3 px-4 text-white text-lg placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                      />
                    </div>
                  </div>

                  <motion.button type="submit"
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg py-4 rounded-2xl shadow-lg shadow-cyan-500/20 border-t border-cyan-400/50 transition-all duration-300 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02, boxShadow: '0 20px 30px -10px rgba(0, 255, 255, 0.25)' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Proceed to Deposit
                    <ArrowRight size={20} />
                  </motion.button>
                  {error && <p className="text-red-400 text-sm text-center">{error}</p>}
                </form>
              ) : (
                <div className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6">
                  <h2 className="text-xl font-bold text-white text-center">Complete Your Deposit</h2>
                  <div className="text-center">
                    <p className="text-slate-400">Amount to Deposit</p>
                    <p className="text-3xl font-bold text-cyan-400">{amount} <span className="text-xl">USDT</span></p>
                  </div>
                  
                  {/* Network selection dropdown */}
                  <div className="relative">
                    <button onClick={() => setNetworkSelectOpen(!networkSelectOpen)} className="w-full flex items-center justify-between bg-gray-900/50 border border-white/10 rounded-lg px-4 py-3">
                      <span className="font-semibold">{selectedNetwork}</span>
                      <ChevronDown size={20} className={`transition-transform ${networkSelectOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {networkSelectOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg"
                        >
                          {depositAddresses.map(addr => (
                            <div key={addr.network} onClick={() => { setSelectedNetwork(addr.network); setNetworkSelectOpen(false); }}
                              className="px-4 py-3 hover:bg-white/5 cursor-pointer"
                            >
                              {addr.network}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="bg-gray-900/50 p-4 rounded-lg text-center break-words">
                    <p className="text-sm text-slate-400 mb-1">Deposit Address</p>
                    <div className="flex items-center justify-center gap-2">
                      <p className="font-mono text-cyan-400 text-sm">{currentAddress}</p>
                      <button onClick={handleCopyAddress} className="text-slate-300 hover:text-white transition-colors">
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>

                  <form onSubmit={handleCompletePayment}>
                    <label htmlFor="screenshot-upload" className="w-full cursor-pointer bg-gray-800/50 border-2 border-dashed border-white/20 rounded-xl p-6 flex flex-col items-center justify-center text-center hover:bg-white/5 transition-colors">
                      <UploadCloud size={32} className="text-slate-400 mb-2" />
                      <p className="font-semibold text-white">Upload Payment Screenshot</p>
                      <p className="text-xs text-slate-400">Click to browse or drag & drop</p>
                      <input id="screenshot-upload" type="file" onChange={handleFileChange} className="hidden" />
                    </label>
                    {fileError && <p className="text-red-400 text-sm mt-2 text-center">{fileError}</p>}
                    {file && <p className="text-green-400 text-sm mt-2 text-center">File selected: {file.name}</p>}

                    <motion.button type="submit" disabled={isSubmitting}
                      className="w-full mt-6 bg-green-600 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all hover:bg-green-500 disabled:opacity-50"
                      whileTap={{ scale: 0.98 }}
                    >
                      {isSubmitting ? <><Loader size={20} className="animate-spin" /> Submitting...</> : "Complete Payment"}
                    </motion.button>
                  </form>
                  <button onClick={() => setShowDepositSection(false)} className="w-full text-slate-400 hover:text-white text-sm text-center mt-2">Back</button>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {historyLoading && <div className="text-center py-8"><Loader className="animate-spin inline-block" /></div>}
              {historyError && <p className="text-red-400 text-center">{historyError}</p>}
              {!historyLoading && !historyError && depositHistory.length === 0 && <p className="text-slate-400 text-center py-8">No deposit history found.</p>}
              
              {depositHistory.map(deposit => {
                const status = getStatusColor(deposit.status);
                return (
                  <motion.div key={deposit._id}
                    className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-lg p-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-lg font-bold text-white">{deposit.amount.toFixed(2)} <span className="text-base text-slate-400">USDT</span></p>
                        <p className="text-xs text-slate-500">{new Date(deposit.createdAt).toLocaleString()}</p>
                      </div>
                      <div className={`flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full ${status.bgColor} ${status.color}`}>
                        {status.icon}
                        <span>{status.text}</span>
                      </div>
                    </div>
                    {deposit.screenshot && (
                       <button onClick={() => openImagePopup(deposit.screenshot)} className="text-xs text-cyan-400 hover:underline mt-2">View Screenshot</button>
                    )}
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isPopupOpen && (
            <motion.div 
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.img 
                src={popupImageUrl} 
                alt="Deposit Screenshot" 
                className="max-w-full max-h-full rounded-lg"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
              />
              <button onClick={closeImagePopup} className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2">
                <XCircle size={24} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
};

export default DepositPage;
